// src/vehicles/form.js — форма на автомобил в ТАБОВЕ (не една огромна форма):
// Обща информация · Документи · Застраховки · Бележки.
// Документи/Застраховки са достъпни след първия запис (трябва id).

import { el, field, input, row, clickableRow } from '../shared/dom.js';
import { dateInput, localTodayIso, guardDates } from '../shared/dates.js';
import { dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, tabBar } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state } from '../shared/state.js';

export const CATEGORY_LABELS = { car: 'Лек автомобил', van: 'Бус', truck: 'Товарен', trailer: 'Ремарке' };
export const OWNERSHIP_LABELS = { own: 'Собствен', leasing: 'Лизинг', rented: 'Нает' };
export const STATUS_LABELS = { active: 'Активен', suspended: 'Спрян', sold: 'Продаден' };
export const DOC_STATUS = { valid: ['валиден', 'green'], expiring: ['изтича скоро', 'amber'], expired: ['изтекъл', 'red'], none: ['без срок', 'gray'] };

// Кои видове документи стоят в таб „Застраховки".
const INSURANCE_TYPES = ['Гражданска отговорност', 'Каско'];
const OTHER_TYPES = ['ГТП', 'Винетка', 'Данък МПС', 'Тахограф', 'Талон', 'Друго'];

// Техническо обслужване — най-често обслужваните неща.
const SERVICE_TYPES = ['Смяна на масло', 'Маслен филтър', 'Въздушен филтър', 'Купеен филтър',
  'Горивен филтър', 'Ангренажен ремък', 'Гуми', 'Спирачни накладки', 'Спирачни дискове',
  'Акумулатор', 'Антифриз', 'Спирачна течност', 'Свещи', 'Годишен преглед (сервиз)', 'Друго'];
const SERVICE_QUICK = ['Смяна на масло', 'Гуми', 'Спирачни накладки', 'Ангренажен ремък', 'Акумулатор'];
const SERVICE_STATUS = { overdue: ['просрочено', 'red'], soon: ['наближава', 'amber'], ok: ['планирано', 'green'], none: ['—', 'gray'] };

const selectOf = (labels, current) => el('select', {}, ...Object.keys(labels).map((k) =>
  el('option', { value: k, text: labels[k], selected: current === k })));

// onSaved: вика се след успешен запис/триене, за да се преначертае екранът.
export function vehicleForm(existing, onSaved) {
  const it = existing || {};
  let activeTab = 'general';

  const f = {
    reg_number: input({ value: it.reg_number || '', placeholder: 'напр. СВ1234АВ' }),
    vin: input({ value: it.vin || '', placeholder: '17 знака (по избор)', maxlength: '17' }),
    make: input({ value: it.make || '', placeholder: 'напр. Дачия' }),
    model: input({ value: it.model || '', placeholder: 'напр. Докер' }),
    first_registration: dateInput(it.first_registration || ''),
    category: selectOf(CATEGORY_LABELS, it.category || 'car'),
    fuel: input({ value: it.fuel || '', placeholder: 'напр. дизел' }),
    fuel_norm_l_100: input({ type: 'number', step: '0.1', min: '0', value: it.fuel_norm_l_100 || '' }),
    tank_l: input({ type: 'number', min: '0', value: it.tank_l || '' }),
    odometer_km: input({ type: 'number', min: '0', value: it.odometer_km || '' }),
    ownership: selectOf(OWNERSHIP_LABELS, it.ownership || 'own'),
    driver_name: input({ value: it.driver_name || '', placeholder: 'Само име (без ЕГН)' }),
    status: selectOf(STATUS_LABELS, it.status || 'active'),
    notes: el('textarea', { rows: 6, placeholder: 'Свободни бележки за автомобила…' }, it.notes || ''),
  };

  // Обвивка: лентата се прави наново при всяка смяна (draw), мястото остава.
  const tabsBar = el('div');
  const tabBody = el('div', {});

  function draw() {
    const saved = !!it.id;
    // Разделите освен „Обща информация" и „Бележки" искат записан автомобил.
    const need = (id, label) => ({ id, label, disabled: !saved, title: saved ? '' : 'Първо запишете автомобила' });
    tabsBar.replaceChildren(tabBar([
      { id: 'general', label: 'Обща информация' },
      need('docs', 'Документи'), need('insurance', 'Застраховки'), need('service', 'Обслужване'),
      need('waybills', 'Пътни листа'), need('expenses', 'Разходи'), need('history', 'История'),
      { id: 'notes', label: 'Бележки' },
    ], activeTab, (id) => { activeTab = id; draw(); }, 'Раздели на автомобила'));

    tabBody.innerHTML = '';
    if (activeTab === 'service') {
      drawServiceTab(tabBody, it, onSaved);
    } else if (activeTab === 'waybills') {
      drawWaybillsTab(tabBody, it, onSaved);
    } else if (activeTab === 'expenses') {
      drawExpensesTab(tabBody, it, onSaved);
    } else if (activeTab === 'history') {
      drawHistoryTab(tabBody, it);
    } else if (activeTab === 'general') {
      tabBody.appendChild(el('div', {},
        row(field('Рег. номер', f.reg_number), field('VIN (рама)', f.vin)),
        row(field('Марка', f.make), field('Модел', f.model)),
        row(field('Първа регистрация', f.first_registration), field('Категория', f.category)),
        row(field('Гориво', f.fuel), field('Норма л/100 км', f.fuel_norm_l_100), field('Резервоар (л)', f.tank_l)),
        row(field('Километраж', f.odometer_km), field('Собственост', f.ownership), field('Статус', f.status)),
        field('Шофьор (само име)', f.driver_name)
      ));
    } else if (activeTab === 'notes') {
      tabBody.appendChild(field('Бележки', f.notes));
    } else {
      drawDocsTab(tabBody, it, activeTab === 'insurance', onSaved);
    }
  }

  const save = async (force) => {
    // Неразбрана дата не тръгва празна — сървърът би сложил днешна (ПРОМТ-19).
    if (!guardDates(f.first_registration)) return;
    const p = {
      company_id: it.company_id || state().activeCompany,
      reg_number: f.reg_number.value, vin: f.vin.value, make: f.make.value, model: f.model.value,
      first_registration: f.first_registration.dataset.iso || '', category: f.category.value,
      fuel: f.fuel.value, fuel_norm_l_100: Number(f.fuel_norm_l_100.value) || 0,
      tank_l: Number(f.tank_l.value) || 0, odometer_km: Number(f.odometer_km.value) || 0,
      ownership: f.ownership.value, driver_name: f.driver_name.value,
      status: f.status.value, notes: f.notes.value,
      force: force === true,
    };
    if (!p.reg_number.trim()) { toast('Въведете регистрационен номер.', true); return; }
    try {
      const saved = it.id
        ? await api('/vehicles/' + it.id, { method: 'PUT', body: JSON.stringify(p) })
        : await api('/vehicles', { method: 'POST', body: JSON.stringify(p) });
      Object.assign(it, saved);
      toast('Записано.');
      if (onSaved) onSaved();
      draw(); // отключва табовете Документи/Застраховки при нов автомобил
    } catch (e) {
      // Дублиран рег. № → сървърът пита; потвърждение = запис въпреки това.
      if (/вече съществува/.test(e.message)) {
        if (await confirmDialog(e.message + ' Да запиша ли въпреки това?', { okText: 'Запиши' })) return save(true);
        return;
      }
      toast(e.message, true);
    }
  };

  const foot = el('div', {},
    // Триенето на кола е само за админ (сървърът връща 403) — на другите
    // бутонът изобщо не се показва.
    it.id && state().user && state().user.role === 'admin' ? el('button', {
      class: 'btn sm danger', text: 'Изтрий', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли автомобила ' + (it.reg_number || '') + ' заедно с документите му?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/vehicles/' + it.id, { method: 'DELETE' }); closeModal(); toast('Изтрито.'); if (onSaved) onSaved(); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Затвори', onClick: () => { closeModal(); if (onSaved) onSaved(); } }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: () => save(false) })
  );

  draw();
  openModal({
    title: it.id ? ('Автомобил ' + (it.reg_number || '')) : 'Нов автомобил',
    body: el('div', {}, tabsBar, tabBody),
    foot,
    wide: true, // по-широка форма и по-големи полета (както при фактурите)
  });
}

// ---- Табове „Документи" и „Застраховки" -----------------------------------

function drawDocsTab(container, vehicle, insurance, onSaved) {
  const wrap = el('div', {});
  container.appendChild(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  api('/vehicles/' + vehicle.id + '/docs').then((docs) => {
    wrap.innerHTML = '';
    const mine = docs.filter((d) => INSURANCE_TYPES.includes(d.type) === insurance);
    // Бутони за бърз запис на най-честите видове (ГТП/Винетка/Данък или
    // ГО/Каско) — един клик отваря формата с готов вид.
    const quick = insurance ? INSURANCE_TYPES : ['ГТП', 'Винетка', 'Данък МПС'];
    const chips = el('div', { class: 'doc-chips' },
      ...quick.map((t) => el('button', { class: 'btn sm ghost', type: 'button', text: '+ ' + t, onClick: () => docForm(vehicle, null, insurance, onSaved, t) })));
    wrap.appendChild(el('div', { style: 'display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap' },
      chips,
      el('button', { class: 'btn sm', text: insurance ? '+ Друга застраховка' : '+ Друг документ', onClick: () => docForm(vehicle, null, insurance, onSaved) })));
    if (!mine.length) {
      wrap.appendChild(emptyState('▤', insurance ? 'Още няма застраховки.' : 'Още няма документи.'));
      return;
    }
    const tb = el('tbody');
    mine.forEach((d) => {
      const st = DOC_STATUS[d.doc_status] || DOC_STATUS.none;
      const unpaid = (d.installments || []).filter((i) => !i.paid_at).length;
      tb.appendChild(clickableRow(() => docForm(vehicle, d, insurance, onSaved), 'Отвори документ: ' + d.type,
        el('td', { text: d.type }),
        el('td', { text: d.provider || '—' }),
        el('td', {}, d.valid_to ? el('span', { class: 'tag ' + st[1], text: dfmt(d.valid_to) + ' · ' + st[0] }) : '—'),
        el('td', { class: 'hide-sm' }, unpaid ? el('span', { class: 'tag amber', text: unpaid + ' неплатени вноски' }) : (d.installments && d.installments.length ? el('span', { class: 'tag green', text: 'платено' }) : '—'))
      ));
    });
    wrap.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', text: insurance ? 'Застраховател' : 'Издател' }), el('th', { scope: 'col', text: 'Валиден до' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Вноски' }))), tb));
  }).catch((e) => { wrap.innerHTML = ''; wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); });
}

// Формата на документ/застраховка (отделен модал; връща се към колата).
// presetType: избран вид за нов документ (от бутоните за бърз запис).
function docForm(vehicle, existing, insurance, onSaved, presetType) {
  const it = existing || {};
  const types = insurance ? INSURANCE_TYPES : OTHER_TYPES;
  const chosen = it.type || presetType || types[0];
  const f = {
    type: el('select', {}, ...types.map((t) => el('option', { value: t, text: t, selected: chosen === t }))),
    provider: input({ value: it.provider || '', placeholder: insurance ? 'Застраховател' : 'Издател / институция' }),
    agent_name: input({ value: it.agent_name || '', placeholder: 'Агент (по избор)' }),
    agent_phone: input({ value: it.agent_phone || '', placeholder: 'Телефон на агента' }),
    policy_number: input({ value: it.policy_number || '', placeholder: '№ на полица/документ' }),
    valid_from: dateInput(it.valid_from || ''),
    valid_to: dateInput(it.valid_to || ''),
    total_amount: input({ type: 'number', step: '0.01', min: '0', value: it.total_amount || '' }),
    currency: input({ value: it.currency || 'EUR', maxlength: '10', style: 'max-width:90px' }),
    notes: el('textarea', { rows: 3 }, it.notes || ''),
  };

  // Вноски: динамични редове { due_date, amount, платена }.
  const instBox = el('div', {});
  const instRows = [];
  function addInstRow(inst) {
    const r = {
      due_date: dateInput((inst && inst.due_date) || ''),
      amount: input({ type: 'number', step: '0.01', min: '0', value: (inst && inst.amount) || '', placeholder: 'сума' }),
      paid: el('input', { type: 'checkbox', checked: !!(inst && inst.paid_at) }),
      paid_at: (inst && inst.paid_at) || '',
    };
    instRows.push(r);
    instBox.appendChild(el('div', { style: 'display:flex;gap:8px;align-items:center;margin-bottom:6px' },
      r.due_date, r.amount,
      el('label', { style: 'display:flex;gap:5px;align-items:center;font-size:13px;color:var(--muted)' }, r.paid, 'платена'),
      el('button', { type: 'button', class: 'btn sm ghost', text: '×', title: 'Махни реда', onClick: (e) => { e.target.closest('div').remove(); r.removed = true; } })
    ));
  }
  (it.installments || []).forEach(addInstRow);

  const body = el('div', {},
    row(field('Вид', f.type), field(insurance ? 'Застраховател' : 'Издател', f.provider)),
    insurance ? row(field('Агент', f.agent_name), field('Телефон', f.agent_phone)) : null,
    row(field('№ полица/документ', f.policy_number)),
    row(field('Валиден от', f.valid_from), field('Валиден до', f.valid_to)),
    row(field('Обща сума', f.total_amount), field('Валута', f.currency)),
    el('div', { class: 'field' }, el('label', { text: 'Вноски (падежи)' }), instBox,
      el('button', { type: 'button', class: 'btn sm ghost', text: '+ Добави вноска', onClick: () => addInstRow(null) })),
    field('Бележки', f.notes)
  );

  const save = async () => {
    // Неразбрана дата не тръгва празна — сървърът би сложил днешна (ПРОМТ-19).
    if (!guardDates(f.valid_from)) return;
    // Ред със сума или отметка, но БЕЗ дата, не бива да изчезва тихо.
    // (Датата е в dataset.iso — полето показва ДД.ММ.ГГГГ, пази ISO.)
    const halfFilled = instRows.some((r) => !r.removed && !r.due_date.dataset.iso && (r.amount.value || r.paid.checked));
    if (halfFilled) { toast('Има вноска без дата — въведете дата на падежа или махнете реда.', true); return; }
    const installments = instRows.filter((r) => !r.removed && r.due_date.dataset.iso).map((r, i) => ({
      no: i + 1,
      due_date: r.due_date.dataset.iso,
      amount: Number(r.amount.value) || 0,
      paid_at: r.paid.checked ? (r.paid_at || localTodayIso()) : '',
    }));
    const p = {
      type: f.type.value, provider: f.provider.value, agent_name: f.agent_name.value,
      agent_phone: f.agent_phone.value, policy_number: f.policy_number.value,
      valid_from: f.valid_from.dataset.iso || '', valid_to: f.valid_to.dataset.iso || '',
      total_amount: Number(f.total_amount.value) || 0, currency: f.currency.value,
      notes: f.notes.value, installments,
    };
    try {
      if (it.id) await api('/vehicles/' + vehicle.id + '/docs/' + it.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vehicles/' + vehicle.id + '/docs', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано.');
      vehicleFormBack();
    } catch (e) { toast(e.message, true); }
  };

  // Връщане към формата на колата, на съответния таб.
  function vehicleFormBack() {
    closeModal();
    vehicleForm(vehicle, onSaved);
  }

  const foot = el('div', {},
    it.id ? el('button', {
      class: 'btn sm danger', text: 'Изтрий', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли този запис?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/vehicles/' + vehicle.id + '/docs/' + it.id, { method: 'DELETE' }); toast('Изтрито.'); vehicleFormBack(); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Назад', onClick: vehicleFormBack }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })
  );

  closeModal();
  openModal({ title: (insurance ? 'Застраховка' : 'Документ') + ' — ' + (vehicle.reg_number || ''), body, foot, wide: true });
}

// ---- Таб „Обслужване" (км/кога/какво) -------------------------------------

function nextLabel(due) {
  const parts = [];
  if (due.next_date) parts.push('на ' + dfmt(due.next_date) + (due.days != null ? (due.days < 0 ? ' (просрочено)' : ' (след ' + due.days + ' дни)') : ''));
  if (due.next_odometer_km) parts.push('при ' + due.next_odometer_km + ' км' + (due.km_left != null ? (due.km_left < 0 ? ' (надминато)' : ' (след ' + due.km_left + ' км)') : ''));
  return parts.join(' · ') || '—';
}

function drawServiceTab(container, vehicle, onSaved) {
  const wrap = el('div', {});
  container.appendChild(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  api('/vehicles/' + vehicle.id + '/service').then((rows) => {
    wrap.innerHTML = '';
    const chips = el('div', { class: 'doc-chips' },
      ...SERVICE_QUICK.map((t) => el('button', { class: 'btn sm ghost', type: 'button', text: '+ ' + t, onClick: () => serviceForm(vehicle, null, onSaved, t) })));
    wrap.appendChild(el('div', { style: 'display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap' },
      chips,
      el('button', { class: 'btn sm', text: '+ Друго обслужване', onClick: () => serviceForm(vehicle, null, onSaved) })));

    if (!rows.length) { wrap.appendChild(emptyState('⚙', 'Още няма записано обслужване.')); return; }
    const tb = el('tbody');
    rows.forEach((s) => {
      const st = SERVICE_STATUS[(s.due && s.due.status) || 'none'] || SERVICE_STATUS.none;
      tb.appendChild(clickableRow(() => serviceForm(vehicle, s, onSaved), 'Отвори обслужване: ' + s.type,
        el('td', { text: s.type }),
        el('td', { text: s.date ? dfmt(s.date) : '—' }),
        el('td', { class: 'hide-sm num', text: s.odometer_km ? s.odometer_km + ' км' : '—' }),
        el('td', {}, s.due && s.due.status !== 'none' ? el('span', { class: 'tag ' + st[1], text: st[0] }) : '—'),
        el('td', { class: 'hide-sm', text: nextLabel(s.due || {}) })
      ));
    });
    wrap.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Какво' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', class: 'hide-sm num', text: 'Километри' }),
      el('th', { scope: 'col', text: 'Статус' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Следващо' }))), tb));
  }).catch((e) => { wrap.innerHTML = ''; wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); });
}

function serviceForm(vehicle, existing, onSaved, presetType) {
  const it = existing || {};
  const chosen = it.type || presetType || SERVICE_TYPES[0];
  const f = {
    type: el('select', {}, ...SERVICE_TYPES.map((t) => el('option', { value: t, text: t, selected: chosen === t }))),
    date: dateInput(it.date || localTodayIso()),
    odometer_km: input({ type: 'number', min: '0', value: it.odometer_km || vehicle.odometer_km || '', placeholder: 'км при обслужването' }),
    next_date: dateInput(it.next_date || ''),
    next_odometer_km: input({ type: 'number', min: '0', value: it.next_odometer_km || '', placeholder: 'км за следващото' }),
    cost: input({ type: 'number', step: '0.01', min: '0', value: it.cost || '', placeholder: 'цена' }),
    currency: input({ value: it.currency || 'EUR', maxlength: '10', style: 'max-width:90px' }),
    notes: el('textarea', { rows: 3 }, it.notes || ''),
  };

  const body = el('div', {},
    row(field('Какво е направено', f.type)),
    row(field('Дата', f.date), field('Километри сега', f.odometer_km)),
    el('div', { class: 'cal-hint', text: 'Кога е следващото — по дата, по километри или и двете (напр. масло на 12 месеца или на 15 000 км):' }),
    row(field('Следваща дата', f.next_date), field('Следващи километри', f.next_odometer_km)),
    row(field('Цена', f.cost), field('Валута', f.currency)),
    field('Бележки', f.notes)
  );

  const save = async () => {
    const p = {
      type: f.type.value, date: f.date.dataset.iso || '',
      odometer_km: Number(f.odometer_km.value) || 0,
      next_date: f.next_date.dataset.iso || '', next_odometer_km: Number(f.next_odometer_km.value) || 0,
      cost: Number(f.cost.value) || 0, currency: f.currency.value, notes: f.notes.value,
    };
    if (!p.date) { toast('Изберете дата на обслужването.', true); return; }
    try {
      if (it.id) await api('/vehicles/' + vehicle.id + '/service/' + it.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vehicles/' + vehicle.id + '/service', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано.');
      back();
    } catch (e) { toast(e.message, true); }
  };

  function back() { closeModal(); vehicleForm(vehicle, onSaved); }

  const foot = el('div', {},
    it.id ? el('button', {
      class: 'btn sm danger', text: 'Изтрий', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли този запис за обслужване?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/vehicles/' + vehicle.id + '/service/' + it.id, { method: 'DELETE' }); toast('Изтрито.'); back(); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Назад', onClick: back }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })
  );

  closeModal();
  openModal({ title: 'Обслужване — ' + (vehicle.reg_number || ''), body, foot, wide: true });
}

// ---- Таб „Пътни листа" (Фаза 2) -------------------------------------------

function drawWaybillsTab(container, vehicle, onSaved) {
  const wrap = el('div', {});
  container.appendChild(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  api('/vehicles/' + vehicle.id + '/waybills').then((rows) => {
    wrap.innerHTML = '';
    wrap.appendChild(el('div', { style: 'display:flex;justify-content:flex-end;gap:8px;margin-bottom:10px;flex-wrap:wrap' },
      rows.length ? el('button', {
        class: 'btn sm ghost', text: '⧉ Копирай последния',
        title: 'Нов лист като най-новия: същият маршрут, километрите продължават',
        onClick: async () => {
          try { waybillForm(vehicle, null, onSaved, await api('/vehicles/' + vehicle.id + '/waybills/copy-last')); }
          catch (e) { toast(e.message, true); }
        },
      }) : null,
      el('button', { class: 'btn sm', text: '+ Нов пътен лист', onClick: () => waybillForm(vehicle, null, onSaved) })));

    if (!rows.length) { wrap.appendChild(emptyState('⇥', 'Още няма пътни листа.')); return; }
    const tb = el('tbody');
    rows.forEach((w) => {
      tb.appendChild(clickableRow(() => waybillForm(vehicle, w, onSaved), 'Отвори пътен лист № ' + (w.number || ''),
        el('td', { text: '№ ' + (w.number || '') }),
        el('td', { text: dfmt(w.date_from) + (w.date_to && w.date_to !== w.date_from ? ' – ' + dfmt(w.date_to) : '') }),
        el('td', { class: 'hide-sm', text: w.driver_name || '—' }),
        el('td', { class: 'num', text: (w.distance_km || 0) + ' км' }),
        el('td', { class: 'hide-sm num', text: (w.fuel_used_l || 0) + ' л' })
      ));
    });
    wrap.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Период' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Водач' }),
      el('th', { scope: 'col', class: 'num', text: 'Километри' }), el('th', { scope: 'col', class: 'hide-sm num', text: 'Гориво' }))), tb));
  }).catch((e) => { wrap.innerHTML = ''; wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); });
}

// ---- Таб „История" (Фаза 2): обща хронология, само за четене --------------

const HISTORY_KIND = {
  doc: ['Документ', 'gray'],
  service: ['Обслужване', 'amber'],
  waybill: ['Пътен лист', 'green'],
  expense: ['Разход', 'red'],
};

function drawHistoryTab(container, vehicle) {
  const wrap = el('div', {});
  container.appendChild(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  api('/vehicles/' + vehicle.id + '/history').then((rows) => {
    wrap.innerHTML = '';
    if (!rows.length) { wrap.appendChild(emptyState('◷', 'Още няма нищо записано за този автомобил.')); return; }
    const tb = el('tbody');
    rows.forEach((h) => {
      const kind = HISTORY_KIND[h.kind] || ['—', 'gray'];
      tb.appendChild(el('tr', {},
        el('td', { text: h.date ? dfmt(h.date) : '—' }),
        el('td', {}, el('span', { class: 'tag ' + kind[1], text: kind[0] })),
        el('td', { text: h.label }),
        el('td', { class: 'hide-sm', text: h.detail || '—' }),
        el('td', { class: 'num', text: h.amount ? (Number(h.amount) || 0).toFixed(2) + ' ' + (h.currency || 'EUR') : '—' })
      ));
    });
    wrap.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', text: 'Какво' }),
      el('th', { scope: 'col', class: 'hide-sm', text: 'Подробности' }), el('th', { scope: 'col', class: 'num', text: 'Сума' }))), tb));
  }).catch((e) => { wrap.innerHTML = ''; wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); });
}

// ---- Таб „Разходи" (Фаза 2) -----------------------------------------------

const EXPENSE_TYPES = ['Гориво', 'Ремонт', 'Масла и консумативи', 'Гуми', 'Автомивка', 'Паркинг', 'Тол такса', 'Глоба', 'Друго'];
const EXPENSE_QUICK = ['Гориво', 'Паркинг', 'Тол такса', 'Автомивка'];

function drawExpensesTab(container, vehicle, onSaved) {
  const wrap = el('div', {});
  container.appendChild(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  api('/vehicles/' + vehicle.id + '/expenses').then((rows) => {
    wrap.innerHTML = '';
    const chips = el('div', { class: 'doc-chips' },
      ...EXPENSE_QUICK.map((t) => el('button', { class: 'btn sm ghost', type: 'button', text: '+ ' + t, onClick: () => expenseForm(vehicle, null, onSaved, t) })));
    wrap.appendChild(el('div', { style: 'display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap' },
      chips,
      el('button', { class: 'btn sm', text: '+ Друг разход', onClick: () => expenseForm(vehicle, null, onSaved) })));

    if (!rows.length) { wrap.appendChild(emptyState('¤', 'Още няма записани разходи.')); return; }
    const tb = el('tbody');
    rows.forEach((x) => {
      tb.appendChild(clickableRow(() => expenseForm(vehicle, x, onSaved), 'Отвори разход: ' + x.type,
        el('td', { text: dfmt(x.date) }),
        el('td', { text: x.type }),
        el('td', { class: 'hide-sm', text: x.supplier || '—' }),
        el('td', { class: 'num', text: (Number(x.amount) || 0).toFixed(2) + ' ' + (x.currency || 'EUR') }),
        el('td', { class: 'hide-sm', text: x.invoice_id ? 'към фактура' : '—' })
      ));
    });
    wrap.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Доставчик' }),
      el('th', { scope: 'col', class: 'num', text: 'Сума' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Фактура' }))), tb));
  }).catch((e) => { wrap.innerHTML = ''; wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); });
}

function expenseForm(vehicle, existing, onSaved, presetType) {
  const it = existing || {};
  const chosen = it.type || presetType || EXPENSE_TYPES[0];
  const f = {
    date: dateInput(it.date || localTodayIso()),
    type: el('select', {}, ...EXPENSE_TYPES.map((t) => el('option', { value: t, text: t, selected: chosen === t }))),
    amount: input({ type: 'number', step: '0.01', min: '0', value: it.amount || '', placeholder: 'сума с ДДС' }),
    vat: input({ type: 'number', step: '0.01', min: '0', value: it.vat || '', placeholder: 'от тях ДДС (по избор)' }),
    currency: input({ value: it.currency || 'EUR', maxlength: '10', style: 'max-width:90px' }),
    supplier: input({ value: it.supplier || '', placeholder: 'бензиностанция / сервиз / …' }),
    odometer_km: input({ type: 'number', min: '0', value: it.odometer_km || '', placeholder: 'км (по избор)' }),
    liters: input({ type: 'number', step: '0.01', min: '0', value: it.liters || '', placeholder: 'литри (при гориво)' }),
    invoice_id: el('select', {}, el('option', { value: '', text: '— без фактура (дребен разход) —' })),
    notes: el('textarea', { rows: 3 }, it.notes || ''),
  };

  // Текущо свързаната фактура е опция ОЩЕ ОТ СТАРТА (и избрана) — дори
  // списъкът да не се зареди или тя да е извън последните 100, „Запази"
  // не бива тихо да откача връзката.
  if (it.invoice_id) {
    f.invoice_id.appendChild(el('option', { value: it.invoice_id, text: 'фактура № ' + it.invoice_id, selected: true }));
  }

  // Фактурите на фирмата (последните 100): разходът може да СОЧИ фактура,
  // за да не влиза един разход два пъти в системата.
  api('/invoices?company_id=' + vehicle.company_id).then((list) => {
    const label = (inv) => '№ ' + (inv.number || inv.id) + (inv.date ? ' · ' + dfmt(inv.date) : '');
    const cur = it.invoice_id ? [...f.invoice_id.options].find((o) => Number(o.value) === Number(it.invoice_id)) : null;
    if (cur) {
      const inv = (list || []).find((i) => Number(i.id) === Number(it.invoice_id));
      if (inv) cur.textContent = label(inv);
    }
    (list || [])
      .slice()
      .sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')))
      .slice(0, 100)
      .forEach((inv) => {
        if (Number(inv.id) === Number(it.invoice_id)) return; // вече е горе
        f.invoice_id.appendChild(el('option', { value: inv.id, text: label(inv) }));
      });
  }).catch(() => {}); // при грешка изборът пази текущата връзка

  const body = el('div', {},
    row(field('Дата', f.date), field('Вид', f.type)),
    row(field('Сума', f.amount), field('ДДС', f.vat), field('Валута', f.currency)),
    row(field('Доставчик', f.supplier), field('Километраж', f.odometer_km), field('Литри', f.liters)),
    field('Свързана фактура (по избор)', f.invoice_id),
    el('div', { class: 'cal-hint', text: 'Ако разходът вече е въведен като фактура — посочи я тук, вместо да го пишеш втори път.' }),
    field('Бележки', f.notes)
  );

  const save = async () => {
    const p = {
      date: f.date.dataset.iso || '', type: f.type.value,
      amount: Number(f.amount.value) || 0, vat: Number(f.vat.value) || 0,
      currency: f.currency.value, supplier: f.supplier.value,
      odometer_km: Number(f.odometer_km.value) || 0, liters: Number(f.liters.value) || 0,
      invoice_id: f.invoice_id.value ? Number(f.invoice_id.value) : null,
      notes: f.notes.value,
    };
    if (!p.date) { toast('Изберете дата на разхода.', true); return; }
    try {
      if (it.id) await api('/vehicles/' + vehicle.id + '/expenses/' + it.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vehicles/' + vehicle.id + '/expenses', { method: 'POST', body: JSON.stringify(p) });
      // Огледай сървърния bumpOdometer в клиентския обект (виж пътните листа).
      vehicle.odometer_km = Math.max(Number(vehicle.odometer_km) || 0, p.odometer_km || 0);
      toast('Записано.');
      back();
    } catch (e) { toast(e.message, true); }
  };

  function back() { closeModal(); vehicleForm(vehicle, onSaved); }

  const foot = el('div', {},
    it.id ? el('button', {
      class: 'btn sm danger', text: 'Изтрий', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли този разход?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/vehicles/' + vehicle.id + '/expenses/' + it.id, { method: 'DELETE' }); toast('Изтрито.'); back(); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Назад', onClick: back }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })
  );

  closeModal();
  openModal({ title: 'Разход — ' + (vehicle.reg_number || ''), body, foot, wide: true });
}

// prefill: заготовка от „Копирай последния" (нов запис с готови стойности).
function waybillForm(vehicle, existing, onSaved, prefill) {
  const it = existing || prefill || {};
  const isNew = !existing || !existing.id;
  const today = localTodayIso();

  const f = {
    date_from: dateInput(it.date_from || today),
    date_to: dateInput(it.date_to || today),
    driver_name: input({ value: it.driver_name || vehicle.driver_name || '', placeholder: 'Само име (без ЕГН)' }),
    start_km: input({ type: 'number', min: '0', value: it.start_km != null && it.start_km !== 0 ? it.start_km : (isNew ? vehicle.odometer_km || '' : it.start_km) }),
    end_km: input({ type: 'number', min: '0', value: it.end_km || '' }),
    fuel_norm_l_100: input({ type: 'number', step: '0.1', min: '0', value: (it.fuel_norm_l_100 != null && it.fuel_norm_l_100 !== 0) ? it.fuel_norm_l_100 : (vehicle.fuel_norm_l_100 || '') }),
    notes: el('textarea', { rows: 3 }, it.notes || ''),
  };

  // Маршрут: динамични редове { date, from, to, purpose, km }.
  const routeBox = el('div', {});
  const routeRows = [];
  function addRouteRow(r) {
    const rr = {
      date: dateInput((r && r.date) || f.date_from.dataset.iso || today),
      from: input({ value: (r && r.from) || '', placeholder: 'от' }),
      to: input({ value: (r && r.to) || '', placeholder: 'до' }),
      purpose: input({ value: (r && r.purpose) || '', placeholder: 'цел / товар' }),
      km: input({ type: 'number', step: '0.1', min: '0', value: (r && r.km) || '', placeholder: 'км', style: 'max-width:90px' }),
    };
    rr.km.addEventListener('input', recompute);
    routeRows.push(rr);
    routeBox.appendChild(el('div', { style: 'display:flex;gap:6px;align-items:center;margin-bottom:6px;flex-wrap:wrap' },
      rr.date, rr.from, rr.to, rr.purpose, rr.km,
      el('button', { type: 'button', class: 'btn sm ghost', text: '×', title: 'Махни реда', onClick: (e) => { e.target.closest('div').remove(); rr.removed = true; recompute(); } })
    ));
  }
  ((it.route && it.route.length) ? it.route : [null]).forEach(addRouteRow);

  // Живата сметка: километри (край−начало или сборът на маршрута) и гориво.
  // ОГЛЕДАЛО на сървъра (services/waybills.js): същото клампване (без
  // отрицателни км, до 60 реда) и СЪЩАТА формула на закръгляне — иначе
  // прегледът и записаното се разминават със стотинка.
  const calcOut = el('div', { class: 'cal-hint', style: 'margin:8px 0' });
  function recompute() {
    const s = Math.max(0, Number(f.start_km.value) || 0), e = Math.max(0, Number(f.end_km.value) || 0);
    const routeSum = routeRows.filter((r) => !r.removed).slice(0, 60)
      .reduce((sum, r) => sum + Math.max(0, Number(r.km.value) || 0), 0);
    const dist = Math.round(((s > 0 && e > 0) ? (e - s) : routeSum) * 100) / 100;
    // Празна норма = сървърът ще вземе тази на записа/колата — показваме нея.
    const norm = f.fuel_norm_l_100.value === ''
      ? (Number(!isNew ? existing.fuel_norm_l_100 : vehicle.fuel_norm_l_100) || 0)
      : (Number(f.fuel_norm_l_100.value) || 0);
    const fuel = Math.round(dist * norm / 100 * 100) / 100;
    calcOut.textContent = 'Пробег: ' + dist + ' км · Гориво по норма: ' + fuel.toFixed(2) + ' л (' + norm + ' л/100 км)';
  }
  ['start_km', 'end_km', 'fuel_norm_l_100'].forEach((k) => f[k].addEventListener('input', recompute));
  recompute();

  const body = el('div', {},
    row(field('От дата', f.date_from), field('До дата', f.date_to), field('Водач', f.driver_name)),
    row(field('Км при тръгване', f.start_km), field('Км при връщане', f.end_km), field('Норма л/100 км', f.fuel_norm_l_100)),
    el('div', { class: 'field' }, el('label', { text: 'Маршрут (дата · от · до · цел · км)' }), routeBox,
      el('button', { type: 'button', class: 'btn sm ghost', text: '+ Добави ред', onClick: () => { addRouteRow(null); recompute(); } })),
    calcOut,
    field('Бележки', f.notes)
  );

  const save = async () => {
    const p = {
      date_from: f.date_from.dataset.iso || '', date_to: f.date_to.dataset.iso || '',
      driver_name: f.driver_name.value,
      start_km: Number(f.start_km.value) || 0, end_km: Number(f.end_km.value) || 0,
      fuel_norm_l_100: f.fuel_norm_l_100.value === '' ? '' : (Number(f.fuel_norm_l_100.value) || 0),
      route: routeRows.filter((r) => !r.removed).map((r) => ({
        date: r.date.dataset.iso || '', from: r.from.value, to: r.to.value, purpose: r.purpose.value, km: Number(r.km.value) || 0,
      })),
      notes: f.notes.value,
    };
    if (!p.date_from) { toast('Изберете начална дата.', true); return; }
    try {
      if (!isNew) await api('/vehicles/' + vehicle.id + '/waybills/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vehicles/' + vehicle.id + '/waybills', { method: 'POST', body: JSON.stringify(p) });
      // Сървърът бута километража на колата напред — огледай го и в
      // клиентския обект, иначе повторно „Запази" на колата със стария
      // километраж тихо връща брояча назад.
      vehicle.odometer_km = Math.max(Number(vehicle.odometer_km) || 0, p.end_km || 0);
      toast('Записано.');
      back();
    } catch (e) { toast(e.message, true); }
  };

  function back() { closeModal(); vehicleForm(vehicle, onSaved); }

  const foot = el('div', {},
    !isNew ? el('button', {
      class: 'btn sm ghost', text: 'PDF', style: 'margin-right:auto',
      onClick: () => { window.open('/api/vehicles/' + vehicle.id + '/waybills/' + existing.id + '/pdf', '_blank'); },
    }) : null,
    !isNew ? el('button', {
      class: 'btn sm danger', text: 'Изтрий',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли пътен лист № ' + (existing.number || '') + '?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/vehicles/' + vehicle.id + '/waybills/' + existing.id, { method: 'DELETE' }); toast('Изтрито.'); back(); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Назад', onClick: back, style: !isNew ? 'margin-left:8px' : 'margin-left:auto' }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })
  );

  closeModal();
  openModal({
    title: !isNew ? ('Пътен лист № ' + (existing.number || '') + ' — ' + (vehicle.reg_number || '')) : ('Нов пътен лист — ' + (vehicle.reg_number || '')),
    body, foot, wide: true,
  });
}
